import { apiRequest } from '../../shared/api/client';

/**
 * Customer master, its delivery addresses, and the warehouses it belongs to.
 *
 *   GET    /api/v1/customers[?includeInactive]                 customers:read
 *   GET    /api/v1/customers/{id}                              customers:read
 *   POST   /api/v1/customers                                   customers:write
 *   PATCH  /api/v1/customers/{id}                              customers:write
 *   GET    /api/v1/customers/{id}/ship-tos                     customers:read
 *   PUT    /api/v1/customers/{id}/ship-tos                     customers:write
 *   DELETE /api/v1/customers/{id}/ship-tos/{shipToId}          customers:write
 *   GET    /api/v1/customers/{id}/warehouses                   customers:read
 *   PUT    /api/v1/customers/{id}/warehouses                   customers:write
 *
 * The list is scoped by the warehouse the session is working in — it returns that warehouse's
 * customers plus any assigned nowhere. There is no warehouse filter here as there is on suppliers,
 * because the header already decides it.
 *
 * There is no DELETE for a customer. Sales orders will reference one and a shipped order has to keep
 * saying who it went to, so the screen deactivates instead.
 *
 * No QuickBooks fields. That integration is a paid add-on and its mapping belongs to the add-on.
 */

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export interface CustomerDto {
  customerId: string;
  customerNumber: string;
  customerName: string;
  companyName: string | null;
  /** One field, as legacy has it: splitting means guessing where a name divides. */
  contactName: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  createdAtUtc: string;
}

/** Everything editable. Create adds customerNumber; update cannot change it. */
export interface CustomerInput {
  customerName: string;
  companyName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
}

/**
 * One place a customer takes delivery.
 *
 * Exactly one is the default — the address a sales order offers before anybody chooses. The server
 * holds that invariant, so a save that sets `isDefault` moves the flag rather than adding a second
 * one, and the first address a customer gets becomes the default whatever was asked for.
 */
export interface CustomerShipToDto {
  customerShipToId: string;
  customerId: string;
  /** Unique within this customer. Appears on their paperwork, so ambiguity is real. */
  shipToCode: string;
  shipToName: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  /** A code, not a name — see the state/province reference table. */
  stateProvince: string | null;
  postalCode: string | null;
  countryCode: string;
  /** The number a driver rings at THIS dock, which is why it is on the address. */
  phone: string | null;
  notes: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface CustomerShipToInput {
  /** Omitted to create. */
  customerShipToId?: string;
  shipToCode: string;
  shipToName: string;
  addressLine1: string;
  city: string;
  countryCode: string;
  addressLine2: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  phone: string | null;
  notes: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface CustomerWarehouseDto {
  warehouseId: string;
  warehouseCode: string;
  displayName: string;
  isActive: boolean;
}

export async function listCustomers(
  options: { includeInactive?: boolean } = {},
  signal?: AbortSignal,
): Promise<CustomerDto[]> {
  const query = options.includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<CustomerDto[]>(`/v1/customers/${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createCustomer(
  body: CustomerInput & { customerNumber: string },
): Promise<CustomerDto> {
  const { data } = await apiRequest<CustomerDto>('/v1/customers/', {
    method: 'POST',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateCustomer(id: string, body: CustomerInput): Promise<CustomerDto> {
  const { data } = await apiRequest<CustomerDto>(`/v1/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    ...COOKIE_INIT,
  });
  return data;
}

export async function listShipTos(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerShipToDto[]> {
  const { data } = await apiRequest<CustomerShipToDto[]>(
    `/v1/customers/${encodeURIComponent(customerId)}/ship-tos`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Insert or update, decided by whether the input carries an id. */
export async function saveShipTo(
  customerId: string,
  body: CustomerShipToInput,
): Promise<CustomerShipToDto> {
  const { data } = await apiRequest<CustomerShipToDto>(
    `/v1/customers/${encodeURIComponent(customerId)}/ship-tos`,
    { method: 'PUT', body, ...COOKIE_INIT },
  );
  return data;
}

export async function deleteShipTo(customerId: string, shipToId: string): Promise<void> {
  await apiRequest<void>(
    `/v1/customers/${encodeURIComponent(customerId)}/ship-tos/${encodeURIComponent(shipToId)}`,
    { method: 'DELETE', ...COOKIE_INIT },
  );
}

export async function listCustomerWarehouses(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerWarehouseDto[]> {
  const { data } = await apiRequest<CustomerWarehouseDto[]>(
    `/v1/customers/${encodeURIComponent(customerId)}/warehouses`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Whole-set: what is absent is unassigned.
 *
 * An empty list means the customer is visible in EVERY warehouse, not none — the rule master data
 * follows throughout, and what makes onboarding possible, since a customer is created before
 * anybody assigns it.
 */
export async function setCustomerWarehouses(
  customerId: string,
  warehouseIds: string[],
): Promise<CustomerWarehouseDto[]> {
  const { data } = await apiRequest<CustomerWarehouseDto[]>(
    `/v1/customers/${encodeURIComponent(customerId)}/warehouses`,
    { method: 'PUT', body: { warehouseIds }, ...COOKIE_INIT },
  );
  return data;
}

/** "MAIN — Main dock", or just the name when the code adds nothing. */
export function shipToLabel(shipTo: CustomerShipToDto): string {
  return shipTo.shipToCode === shipTo.shipToName
    ? shipTo.shipToName
    : `${shipTo.shipToCode} — ${shipTo.shipToName}`;
}

/** One line, the way an address goes on an envelope, skipping whatever is blank. */
export function formatShipToAddress(shipTo: CustomerShipToDto): string {
  const region = [shipTo.city, shipTo.stateProvince].filter(Boolean).join(', ');
  return [shipTo.addressLine1, shipTo.addressLine2, region, shipTo.postalCode, shipTo.countryCode]
    .filter(Boolean)
    .join(', ');
}
